import { useEffect, useId, useState, type ComponentProps } from "react";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@devhop/ui";
import { EyeIcon, PrinterIcon } from "lucide-react";
import type { BrandPrintLanguage } from "@/shared/lib/brand-document-print-language";
import type { DocumentPrintSignatureOptions } from "@/shared/lib/document-print-signature-options";

type PerformanceBrandPrintMenuProps = {
  /** เรียกก่อนเปิดหน้าพิมพ์ (เช่น บันทึกส่วนลดที่ค้างอยู่) */
  onBeforePrint?: () => void | Promise<void>;
  buildPrintUrl: (
    lang: BrandPrintLanguage,
    options?: {
      includeStudioStamp?: boolean;
      includeStudioSignature?: boolean;
    },
  ) => string;
  label?: string;
  variant?: ComponentProps<typeof Button>["variant"];
  size?: ComponentProps<typeof Button>["size"];
  className?: string;
  iconClassName?: string;
  showLabel?: boolean;
  /** draft preview uses eye icon + preview label */
  preview?: boolean;
  /**
   * Controlled signature/stamp options from the event detail panel.
   * When set, the print menu uses these values directly (no inline toggle).
   */
  signatureOptions?: DocumentPrintSignatureOptions;
  /**
   * Legacy single toggle inside the dropdown (e.g. DocumentsPage).
   * Prefer signatureOptions on event detail.
   */
  signatureToggle?: {
    defaultChecked?: boolean;
  };
};

export function openPerformanceBrandPrint(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

export function PerformanceBrandPrintMenu({
  buildPrintUrl,
  onBeforePrint,
  label,
  variant = "outline",
  size = "sm",
  className,
  iconClassName = "size-4",
  showLabel = true,
  preview = false,
  signatureOptions,
  signatureToggle,
}: PerformanceBrandPrintMenuProps) {
  const Icon = preview ? EyeIcon : PrinterIcon;
  const buttonLabel = label ?? (preview ? "ເບິ່ງຕົວຢ່າງ" : "ພິມ");
  const signatureCheckboxId = useId();
  const defaultIncludeSignature = signatureToggle?.defaultChecked ?? true;
  const [legacyIncludeSignature, setLegacyIncludeSignature] = useState(
    defaultIncludeSignature,
  );

  useEffect(() => {
    if (!signatureToggle || signatureOptions) return;
    setLegacyIncludeSignature(defaultIncludeSignature);
  }, [signatureToggle, signatureOptions, defaultIncludeSignature]);

  const resolvePrintOptions = (): DocumentPrintSignatureOptions | undefined => {
    if (signatureOptions) return signatureOptions;
    if (signatureToggle) {
      return {
        includeStudioStamp: legacyIncludeSignature,
        includeStudioSignature: legacyIncludeSignature,
      };
    }
    return undefined;
  };

  const openLang = async (lang: BrandPrintLanguage) => {
    await onBeforePrint?.();
    const options = resolvePrintOptions();
    const url = options
      ? buildPrintUrl(lang, {
          includeStudioStamp: options.includeStudioStamp,
          includeStudioSignature: options.includeStudioSignature,
        })
      : buildPrintUrl(lang);
    openPerformanceBrandPrint(url);
  };

  const showLegacyToggle = signatureToggle != null && signatureOptions == null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant={variant} size={size} className={className}>
          <Icon className={iconClassName} />
          {showLabel ? (
            <span className={cn(iconClassName && "ml-1.5", "text-xs")}>
              {buttonLabel}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        {showLegacyToggle ? (
          <>
            <div
              className="flex items-center gap-2 px-2 py-1.5"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Checkbox
                id={signatureCheckboxId}
                checked={legacyIncludeSignature}
                onCheckedChange={(checked) =>
                  setLegacyIncludeSignature(checked === true)
                }
              />
              <label
                htmlFor={signatureCheckboxId}
                className="cursor-pointer text-xs leading-snug"
              >
                ໃສ່ລາຍເຊັນ / ຕາປະທັບ
              </label>
            </div>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem onClick={() => openLang("lo")}>
          ລາວ (LO)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openLang("en")}>
          English (EN)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
